/**
 * 管理后台菜单权限（与路由 meta.menuKey、角色表 t_role.menus JSON 一致）
 *
 * 一级菜单：如 dashboard、roi
 * ROI 二级菜单：roi:promotion | roi:overview | roi:audit
 * 规则：拥有一级 key「roi」= 该一级下全部子菜单；也可只授权部分子 key（可不包含 roi）
 */

/** ROI 一级下二级菜单 key（与 path 一一对应） */
export const ROI_CHILD_MENU_KEYS = ['roi:promotion', 'roi:overview', 'roi:audit'];

/** 供角色编辑树：ROI 子项展示顺序 */
export const ROI_CHILDREN_FOR_TREE = [
  { key: 'roi:promotion', name: '推广账户管理', path: '/roi' },
  { key: 'roi:overview', name: '推广账户概况', path: '/roi-overview' },
  { key: 'roi:audit', name: 'ROI监控', path: '/roi-audit' }
];

/** 一级菜单（无子项的侧边栏项），顺序用于树展示与默认跳转 */
export const TOP_LEVEL_MENU_ITEMS = [
  { key: 'dashboard', name: '首页', path: '/dashboard' },
  { key: 'electronic-map', name: '电子地图', path: '/electronic-map' },
  { key: 'statistics', name: '数据统计', path: '/statistics' },
  { key: 'org', name: '机构管理', path: '/org' },
  { key: 'staff', name: '会员管理', path: '/staff' },
  { key: 'student', name: '学生管理', path: '/student' },
  {
    key: 'roi',
    name: 'ROI管理',
    path: '/roi',
    children: ROI_CHILDREN_FOR_TREE
  },
  { key: 'baidu-aifanfan', name: '百度爱番番', path: '/baidu-aifanfan' },
  { key: 'role', name: '角色管理', path: '/role' },
  { key: 'dict', name: '字典管理', path: '/dict' },
  { key: 'log', name: '日志管理', path: '/log' },
  { key: 'system-settings', name: '系统设置', path: '/system-settings' }
];

/**
 * 扁平顺序：登录后首次可进入路径（含 ROI 子路径）
 */
export const MENU_ROUTE_ORDER = (() => {
  const list = [];
  for (const item of TOP_LEVEL_MENU_ITEMS) {
    if (item.children && item.children.length) {
      for (const c of item.children) {
        list.push({ key: c.key, path: c.path, parentKey: item.key });
      }
    } else {
      list.push({ key: item.key, path: item.path });
    }
  }
  return list;
})();

/** key -> 展示名（含二级） */
export function buildMenuNameMap() {
  const map = {};
  for (const item of TOP_LEVEL_MENU_ITEMS) {
    map[item.key] = item.name;
    if (item.children) {
      for (const c of item.children) {
        map[c.key] = `${item.name} / ${c.name}`;
      }
    }
  }
  return map;
}

export const MENU_NAME_MAP = buildMenuNameMap();

export function getMenuDisplayName(key) {
  if (!key) return '';
  return MENU_NAME_MAP[key] || key;
}

function menuSet(menus) {
  if (!Array.isArray(menus)) return new Set();
  const set = new Set();
  menus.forEach((x) => {
    const s = typeof x === 'string' ? x.trim() : x != null ? String(x).trim() : '';
    if (s) set.add(s);
  });
  return set;
}

/** 是否显示 ROI 一级（子菜单）分组：有一级 roi 或任一 roi: 子 key */
export function hasRoiGroupAccess(menus) {
  const set = menuSet(menus);
  if (set.has('roi')) return true;
  for (const k of ROI_CHILD_MENU_KEYS) {
    if (set.has(k)) return true;
  }
  return false;
}

/**
 * 某菜单项是否可访问：精确 key 命中，或命中其一级父 key（父级表示整组授权）
 */
export function isMenuKeyAllowed(menuKey, menus) {
  if (!menuKey) return true;
  const set = menuSet(menus);
  if (set.has(menuKey)) return true;
  if (menuKey.startsWith('roi:') && set.has('roi')) return true;
  return false;
}

/** 与 isMenuKeyAllowed 相同，供路由守卫、侧栏使用 */
export function isRouteAllowed(menuKey, menus) {
  return isMenuKeyAllowed(menuKey, menus);
}

/** 侧栏不展示但仍可能在角色权限 JSON 中出现的 key（避免首跳落到无入口页） */
const SIDEBAR_HIDDEN_MENU_KEYS = new Set(['role', 'dict', 'log']);

export function getFirstAllowedPath(menus) {
  if (!menus || !menus.length) return null;
  for (const { key, path } of MENU_ROUTE_ORDER) {
    if (SIDEBAR_HIDDEN_MENU_KEYS.has(key)) continue;
    if (isMenuKeyAllowed(key, menus)) return path;
  }
  return null;
}

/**
 * 从 el-tree 勾选结果压缩写入库（建议 getCheckedKeys(true) 仅叶子，避免半选父级歧义）
 * 全选 ROI 下全部叶子等价于只存一级 key「roi」
 */
export function normalizeMenusFromTreeCheckedKeys(leafKeys) {
  const set = new Set(Array.isArray(leafKeys) ? leafKeys : []);
  const out = new Set();
  const roiChildPicked = ROI_CHILD_MENU_KEYS.filter((k) => set.has(k));
  for (const k of set) {
    if (k.startsWith('roi:')) continue;
    out.add(k);
  }
  if (roiChildPicked.length === ROI_CHILD_MENU_KEYS.length) {
    out.add('roi');
  } else {
    roiChildPicked.forEach((k) => out.add(k));
  }
  return [...out];
}

/**
 * 库里的 menus -> el-tree setCheckedKeys（check-strictly=false 时与 Element 父子联动一致）
 */
export function menusToTreeCheckedKeys(menus) {
  const set = menuSet(menus);
  const keys = [];
  if (set.has('roi')) {
    keys.push('roi');
    ROI_CHILD_MENU_KEYS.forEach((k) => keys.push(k));
  } else {
    ROI_CHILD_MENU_KEYS.forEach((k) => {
      if (set.has(k)) keys.push(k);
    });
  }
  for (const item of TOP_LEVEL_MENU_ITEMS) {
    if (item.key === 'roi') continue;
    if (set.has(item.key)) keys.push(item.key);
  }
  return keys;
}
